/**
 * Shared, cached image loading for canvas rendering. Kept free of other core imports so
 * light bundles (storefront garment previews) can use it without the image pipeline.
 */

// Decoded-image cache keyed by src, shared by every canvas render (image layers through
// drawImageItem, product images in useCanvasRenderer, garment mockups in garmentCanvas).
// Renders run per view and again for cart / save exports, so without it every pass
// re-downloads and re-decodes each image.
// In-flight loads are shared too. Large data: URLs are not cached so base64-backed bitmaps are
// not pinned in memory; the oldest entry is evicted past MAX_CACHED_IMAGES.
const imageCache = new Map<string, Promise<HTMLImageElement>>();
const MAX_CACHED_IMAGES = 128;
const MAX_CACHED_DATA_URL = 32 * 1024; // 32KB

function fetchImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // crossOrigin forces CORS and can *taint* the canvas for same-origin
    // URLs when not needed; only set for real cross-origin http(s) URLs.
    if (
      typeof window !== 'undefined' &&
      /^https?:\/\//i.test(src) &&
      !src.startsWith('data:')
    ) {
      try {
        const u = new URL(src, window.location.href);
        if (u.origin !== window.location.origin) {
          img.crossOrigin = 'anonymous';
        }
      } catch {
        img.crossOrigin = 'anonymous';
      }
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

export function loadImageBitmap(src: string): Promise<HTMLImageElement> {
  const cacheable = !src.startsWith('data:') || src.length <= MAX_CACHED_DATA_URL;
  if (!cacheable) return fetchImage(src);

  const cached = imageCache.get(src);
  if (cached) {
    // Refresh recency so eviction drops the least recently used image.
    imageCache.delete(src);
    imageCache.set(src, cached);
    return cached;
  }
  const pending = fetchImage(src);
  imageCache.set(src, pending);
  pending.catch(() => {
    if (imageCache.get(src) === pending) imageCache.delete(src);
  });
  if (imageCache.size > MAX_CACHED_IMAGES) {
    const oldest = imageCache.keys().next().value;
    if (oldest !== undefined) imageCache.delete(oldest);
  }
  return pending;
}
